import React, { useRef } from 'react';
import { Base } from './ALinkStandalone.styles';
import {
  type ALinkStandaloneProps,
  type ALinkStandaloneAnchorProps,
  type ALinkStandaloneButtonProps,
  ALinkStandaloneSize,
} from './ALinkStandalone.types';
import clsx from 'clsx';
import { AIcon, AIconSize } from '../AIcon/index';
import { useTracking } from '../../hooks';

const COMPONENT_NAME = 'a-web-ui-kit-ALinkStandalone';
const TEST_ID = 'web-ui-kit-ALinkStandalone';

const ALinkStandalone = React.forwardRef<HTMLAnchorElement | HTMLButtonElement, ALinkStandaloneProps>((props, ref) => {
  const {
    text,
    children,
    size = ALinkStandaloneSize.Medium,
    className = '',
    externalLink = false,
    trackingProperties,
    custom_click_track_value,
    preTrackVal,
  } = props;
  const defaultRef = useRef<HTMLAnchorElement | HTMLButtonElement>(null);
  const elementRef = (ref ?? defaultRef) as React.RefObject<HTMLAnchorElement | HTMLButtonElement>;

  const extendedClassName = clsx(className, 'm-link', {
    md: size === ALinkStandaloneSize.Medium,
    sm: size === ALinkStandaloneSize.Small,
    xs: size === ALinkStandaloneSize.ExtraSmall,
    ext: externalLink,
  });

  // TODO: Get I18n key for this hardcoded text
  const EXTERNAL_LINK_A11Y_TEXT = ', opens in a new window';

  useTracking({
    ref: elementRef,
    trackingProperties,
    isExternal: externalLink,
    custom_click_track_value,
    preTrackVal,
    text,
  });

  const ExternalIndicator = (
    <AIcon
      size={AIconSize.S}
      icon="icon-external-arrow"
      className="icon-container d-inline-block ml-1"
      aria-label={EXTERNAL_LINK_A11Y_TEXT}
    />
  );
  if ('href' in props && typeof props.href === 'string') {
    // LINK
    const { href, ...anchorRest } = props as ALinkStandaloneAnchorProps;
    const { target } = anchorRest;
    const _target = externalLink ? '_blank' : target;
    // if _target === "_blank" and rel is not provided, suggest noopener noreferrer
    const rel = anchorRest.rel ?? (_target === '_blank' ? 'noopener noreferrer' : undefined);
    const showAsExternal = _target === '_blank';
    return (
      <Base
        as="a"
        href={href}
        ref={elementRef as React.Ref<HTMLAnchorElement>}
        data-component-name={COMPONENT_NAME}
        data-testid={TEST_ID}
        {...anchorRest}
        className={clsx(extendedClassName, { ext: showAsExternal })}
        target={_target}
        rel={rel}
      >
        {children ?? text}
        {showAsExternal && ExternalIndicator}
      </Base>
    );
  } else {
    // BUTTON
    const { href: _maybeHref, ...buttonRest } = props as ALinkStandaloneButtonProps;
    return (
      <Base
        as="button"
        type={buttonRest.type ?? 'button'}
        ref={elementRef as React.Ref<HTMLButtonElement>}
        data-component-name={COMPONENT_NAME}
        data-testid={TEST_ID}
        {...buttonRest}
        className={extendedClassName}
      >
        {children ?? text}
        {externalLink && ExternalIndicator}
      </Base>
    );
  }
});

export { ALinkStandalone, ALinkStandaloneSize, TEST_ID as A_LINK_STANDALONE_TEST_ID };
